import type { AppSelectGroup, AppSelectOption } from '~/components/new/app-ui/app-select'
import { filterOptionsWithCurrent, type OptionsStatus } from './logistics-filter-options'
import { numberOrNull } from './logistics-numbers'

/**
 * Lieux et rattachements des référentiels logistiques (ateliers, hubs, boutiques, transporteurs) :
 * libellés avec repli sur l'id quand la liste n'est pas lisible (droit, chargement, suppression),
 * rattachement atelier / hub, et les clés de sélecteur et d'URL des camions (site `kitchen:<id>` /
 * `hub:<id>`, transporteur `internal` / `carrier:<id>`). Les chauffeurs gardent `HomeSelection`
 * (`HomeSelector`) jusqu'à leur passage aux maquettes. Logique pure, testée par
 * `logistics-places.test.mjs`.
 */

/** Rattachement exclusif atelier / hub, imposé par les CHECK `*_single_home`. */
export type HomeKind = 'kitchen' | 'hub'

export interface HomeSelection {
  homeKind: HomeKind
  /** Id sélectionné, `''` tant qu'aucun ne l'est. */
  homeId: string
}

export const buildHome = ({ homeKind, homeId }: HomeSelection) => ({
  homeCentralKitchenId: homeKind === 'kitchen' ? numberOrNull(homeId) : null,
  homeHubId: homeKind === 'hub' ? numberOrNull(homeId) : null,
})

export const storeLabel = (stores: { id: number; label: string }[], storeId: number): string =>
  stores.find((store) => store.id === storeId)?.label ?? `Boutique #${storeId}`

export const kitchenLabel = (
  kitchens: { id: number; label: string }[],
  id: number | null,
): string =>
  id === null ? '—' : (kitchens.find((kitchen) => kitchen.id === id)?.label ?? `Atelier #${id}`)

/** Nom d'un hub, `Hub #id` s'il n'est pas dans la liste ; `null` seulement sans hub. */
export function hubName(hubs: { id: number; name: string }[], hubId: number): string
export function hubName(hubs: { id: number; name: string }[], hubId: number | null): string | null
export function hubName(hubs: { id: number; name: string }[], hubId: number | null) {
  return hubId === null ? null : (hubs.find((hub) => hub.id === hubId)?.name ?? `Hub #${hubId}`)
}

export const carrierLabel = (carriers: { id: number; name: string }[], carrierId: number): string =>
  carriers.find((carrier) => carrier.id === carrierId)?.name ?? `Transporteur #${carrierId}`

/** Rattachement affiché (badge `AttachmentBadge`, camions et chauffeurs) : `null` = Parc Lucie. */
export const attachmentCarrierName = (
  carrierId: number | null,
  carriers: { id: number; name: string }[],
): string | null => (carrierId === null ? null : carrierLabel(carriers, carrierId))

// --- Clés de sélecteur et d'URL : transporteur, site --------------------------------------------

/** Rattachement : `internal` (Parc Lucie, aucun transporteur) ou `carrier:<id>`. */
export const VEHICLE_ATTACHMENT_KEY_PATTERN = /^(internal|carrier:\d+)$/

export const attachmentKey = (carrierId: number | null): string =>
  carrierId === null ? 'internal' : `carrier:${carrierId}`

/** Transporteur d'une clé : `null` = Parc Lucie, `undefined` = clé mal formée. */
export const parseAttachmentKey = (key: string): number | null | undefined => {
  if (!VEHICLE_ATTACHMENT_KEY_PATTERN.test(key)) return undefined
  return key === 'internal' ? null : Number(key.slice('carrier:'.length))
}

/** Rattachement tel que stocké : exactement un des deux ids est rempli. */
export interface HomeIds {
  homeCentralKitchenId: number | null
  homeHubId: number | null
}

/** Site encodé : `kitchen:<id>` (atelier) ou `hub:<id>`. */
export const SITE_KEY_PATTERN = /^(kitchen|hub):(\d+)$/

export const siteKey = (kind: HomeKind, id: number): string => `${kind}:${id}`

/** `null` pour une clé mal formée. */
export const parseSiteKey = (key: string): { kind: HomeKind; id: number } | null => {
  const match = SITE_KEY_PATTERN.exec(key)
  return match === null ? null : { kind: match[1] as HomeKind, id: Number(match[2]) }
}

/** Rattachement d'une clé de site ; clé mal formée → aucun des deux. */
export const homeOfSiteKey = (key: string): HomeIds => {
  const site = parseSiteKey(key)
  return {
    homeCentralKitchenId: site?.kind === 'kitchen' ? site.id : null,
    homeHubId: site?.kind === 'hub' ? site.id : null,
  }
}

/** Clé de site d'un rattachement (exactement un des deux rempli, CHECK `*_single_home`). */
export const siteKeyOf = (home: HomeIds): string =>
  home.homeCentralKitchenId !== null
    ? siteKey('kitchen', home.homeCentralKitchenId)
    : siteKey('hub', home.homeHubId ?? 0)

/**
 * Clé de site d'un rattachement facultatif (collaborateur : seul un rôle logistique en exige un),
 * `''` sans atelier ni hub plutôt que « hub:0 ».
 */
export const optionalSiteKeyOf = (home: HomeIds): string =>
  home.homeCentralKitchenId === null && home.homeHubId === null ? '' : siteKeyOf(home)

/** Libellé d'un site, avec le même repli que les autres onglets (`Atelier #id`, `Hub #id`). */
export const siteLabelOf = (
  key: string,
  kitchens: { id: number; label: string }[],
  hubs: { id: number; name: string }[],
): { kind: HomeKind; name: string } | null => {
  const site = parseSiteKey(key)
  if (site === null) return null
  return {
    kind: site.kind,
    name: site.kind === 'kitchen' ? kitchenLabel(kitchens, site.id) : hubName(hubs, site.id),
  }
}

/**
 * Groupes « Ateliers » / « Hubs » d'un sélecteur de site, dont la valeur courante reste lisible
 * même absente des options (`filterOptionsWithCurrent`, appliqué au groupe de son type).
 */
export const siteOptionGroups = ({
  kitchens,
  hubs,
  current,
  status,
  labelPrefix = '',
}: {
  kitchens: { id: number; label: string }[]
  hubs: { id: number; name: string }[]
  current: string | undefined
  status: OptionsStatus
  labelPrefix?: string
}): { groups: AppSelectGroup<string>[]; value: string | null } => {
  const currentKind = current === undefined ? undefined : parseSiteKey(current)?.kind
  const withCurrent = (kind: HomeKind, options: AppSelectOption<string>[]) =>
    filterOptionsWithCurrent(
      options,
      currentKind === kind ? current : undefined,
      status,
      (key) => `${labelPrefix}${siteLabelOf(key, kitchens, hubs)?.name ?? key}`,
    )
  const kitchenSelect = withCurrent(
    'kitchen',
    kitchens.map((kitchen) => ({
      label: `${labelPrefix}${kitchen.label}`,
      value: siteKey('kitchen', kitchen.id),
    })),
  )
  const hubSelect = withCurrent(
    'hub',
    hubs.map((hub) => ({ label: `${labelPrefix}${hub.name}`, value: siteKey('hub', hub.id) })),
  )
  return {
    groups: [
      { label: 'Ateliers', options: kitchenSelect.options },
      { label: 'Hubs', options: hubSelect.options },
    ].filter((group) => group.options.length > 0),
    value: kitchenSelect.value ?? hubSelect.value,
  }
}
